import React, { useEffect, useState, useCallback } from "react";
import { Spin, Alert, Table, Tag, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import customClientAxios from "../../customAxios";
import { PlatformAnalyticsUsers } from "../../utils/ApiEndpoints";
import { PageContainer } from "../paUtils";
import styled from "styled-components";

const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 18px 24px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f1f5f9;
`;

const TableTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
`;

const TotalBadge = styled.span`
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 10px;
  border-radius: 20px;
`;

const BrandPagesBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => (p.$count > 0 ? "rgba(99,102,241,0.12)" : "#f1f5f9")};
  color: ${(p) => (p.$count > 0 ? "#6366f1" : "#94a3b8")};
`;

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const ONBOARDING_COLOR = {
  completed: "green",
  pending: "gold",
  brand_page_pending: "blue",
};

const STATUS_COLOR = {
  active: "green",
  inactive: "orange",
  suspended: "red",
};

function UsersTab({ startDate, endDate }) {
  const [data, setData] = useState({ users: [], pagination: {} });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 20 };
      if (search.trim()) params.search = search.trim();
      const res = await customClientAxios().get(PlatformAnalyticsUsers, { params });
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [search]);

  const columns = [
    {
      title: "User",
      key: "user",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{r.name || "—"}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      render: (v) => <span style={{ fontSize: 13 }}>{v || "—"}</span>,
    },
    {
      title: "Account Type",
      dataIndex: "accountType",
      key: "accountType",
      render: (v) => v ? <Tag style={{ fontSize: 11 }}>{v.replace(/_/g, " ")}</Tag> : "—",
    },
    {
      title: "Onboarding",
      dataIndex: "onboardingStatus",
      key: "onboardingStatus",
      render: (v) => v ? (
        <Tag color={ONBOARDING_COLOR[v] || "default"} style={{ fontSize: 11 }}>
          {v.replace(/_/g, " ")}
        </Tag>
      ) : "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => v ? (
        <Tag color={STATUS_COLOR[v] || "default"} style={{ fontSize: 11 }}>{v}</Tag>
      ) : "—",
    },
    {
      title: "Signed Up",
      dataIndex: "signupDate",
      key: "signupDate",
      render: fmtDate,
    },
    {
      title: "Last Login",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      render: (v) => v ? fmtDate(v) : <span style={{ color: "#94a3b8", fontSize: 12 }}>Never</span>,
    },
    {
      title: "Brand Pages",
      key: "bpCount",
      align: "center",
      render: (_, r) => (
        <BrandPagesBadge $count={r.brandPageCount || 0}>
          {r.brandPageCount || 0}
        </BrandPagesBadge>
      ),
    },
  ];

  const pagination = data.pagination || {};

  return (
    <PageContainer>
      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} closable onClose={() => setError(null)} />}

      <TableCard>
        <TableHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <TableTitle>All Users</TableTitle>
            <TotalBadge>{pagination.total ?? 0} users</TotalBadge>
          </div>
          <Input
            placeholder="Search by name, email or mobile..."
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
        </TableHeader>
        <Spin spinning={loading}>
          <Table
            dataSource={data.users || []}
            columns={columns}
            rowKey="userId"
            size="middle"
            pagination={{
              current: page,
              pageSize: 20,
              total: pagination.total || 0,
              onChange: setPage,
              showSizeChanger: false,
              showTotal: (t) => `${t} users`,
            }}
            scroll={{ x: 820 }}
          />
        </Spin>
      </TableCard>
    </PageContainer>
  );
}

export default UsersTab;
